import React, { useEffect, useState } from 'react';
import { useParams, useHistory, Link } from 'react-router-dom';
import styled from 'styled-components';

import { Text, Loader, Layout } from '@humanspace/components';
import { Spacing, Colors } from '@humanspace/foundations';
import { makeRequest } from '@humanspace/infrastructure';
import {
  getProjectAssessmentTypes,
  addProjectAssessmentType,
} from '../../utils/transformers';

const BackLink = styled.span`
  cursor: pointer;
  color: ${Colors.primary};
  font-size: 14px;
  display: inline-block;
  margin-bottom: ${Spacing.m};

  &:hover {
    text-decoration: underline;
  }
`;

const Section = styled.div`
  margin-top: ${Spacing.l};
`;

const SectionTitle = styled(Text)`
  margin-bottom: ${Spacing.s};
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const AssessmentCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  margin-bottom: ${Spacing.xs};
`;

const TypeIndicator = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.color};
  margin-right: 10px;
`;

const TypeLabel = styled.span`
  color: #fff;
  font-size: 15px;
  font-weight: 500;
`;

const OpenButton = styled.button`
  padding: 6px 16px;
  background: ${props => props.color || Colors.primary};
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
`;

const AddButton = styled.button`
  padding: 8px 16px;
  background: transparent;
  color: ${props => props.color || Colors.primary};
  border: 2px solid ${props => props.color || Colors.primary};
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  margin-right: ${Spacing.s};
  margin-top: ${Spacing.xs};

  &:hover {
    background: ${props => (props.color || Colors.primary) + '1a'};
  }
`;

const LoaderWrapper = styled.div`
  height: 250px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const TYPES_CONFIG = {
  human_capital: {
    color: '#F59E0B',
    label: 'Human Capital',
    route: id => `/projects/${id}/details`,
    addLabel: '+ Human Capital',
  },
  brain_health: {
    color: Colors.primary,
    label: 'Brain Health',
    route: id => `/projects/${id}/brain-health`,
    addLabel: '+ Brain Health',
  },
  higher_ed: {
    color: '#6366F1',
    label: 'Higher Education',
    route: id => `/projects/${id}/higher-ed`,
    addLabel: '+ Higher Education',
  },
  healthcare_facility: {
    color: '#0891B2',
    label: 'Healthcare Facility',
    route: id => `/projects/${id}/healthcare`,
    addLabel: '+ Healthcare Facility',
  },
  cities_communities: {
    color: '#0D9488',
    label: 'Cities & Communities',
    route: id => `/projects/${id}/cities`,
    addLabel: '+ Cities & Communities',
  },
  dealer_showroom: {
    color: '#F59E0B',
    label: 'Dealer Showroom',
    route: id => `/projects/${id}/assess`,
    addLabel: '+ Dealer Showroom',
  },
  stress: {
    color: '#E8637A',
    label: 'Stress Model',
    route: (id, stressModelId) =>
      stressModelId ? `/models/${stressModelId}/results` : `/models/new`,
    addLabel: '+ Assess Stress',
  },
};

const ProjectHub = ({ getProject, project, isLoading }) => {
  const { id } = useParams();
  const history = useHistory();
  const [assessmentTypes, setAssessmentTypes] = useState([]);
  const [stressModelId, setStressModelId] = useState(null);

  useEffect(() => {
    getProject(id);
    const localTypes = getProjectAssessmentTypes(id);
    setAssessmentTypes(localTypes);
    setStressModelId(localStorage.getItem('project_stress_model_' + id));

    // Hydrate project types from server if localStorage is empty
    if (localTypes.length === 0) {
      makeRequest({ path: `/projects/${id}/data` })
        .then(data => {
          if (data && data.project_types && Array.isArray(data.project_types)) {
            data.project_types.forEach(t => addProjectAssessmentType(id, t));
            setAssessmentTypes(getProjectAssessmentTypes(id));
          }
        })
        .catch(() => {});
    }
  }, [id, getProject]);

  const handleAddType = type => {
    if (type === 'stress') {
      // For stress, navigate to model creation page
      // After model is created, user returns and it gets linked
      history.push('/models/new');
      return;
    }
    const updated = addProjectAssessmentType(id, type);
    setAssessmentTypes([...updated]);
    // Sync types to server for cross-browser access
    makeRequest({
      path: `/projects/${id}/data/project_types`,
      method: 'PUT',
      body: { value: updated },
      json: true,
    }).catch(() => {});
    // Navigate to the new assessment
    history.push(TYPES_CONFIG[type].route(id));
  };

  const handleOpen = type => {
    const config = TYPES_CONFIG[type];
    if (type === 'stress') {
      history.push(config.route(id, stressModelId));
    } else {
      history.push(config.route(id));
    }
  };

  // Whitelist of assessment types currently offered in the "Add Assessment" section.
  // Add an entry here when a new assessment family is ready for general availability;
  // the corresponding `TYPES_CONFIG` entry stays as-is so existing projects that already
  // have those types continue to render and navigate correctly.
  const VISIBLE_ADD_TYPES = ['human_capital', 'higher_ed'];

  const availableTypes = Object.keys(TYPES_CONFIG).filter(
    t => !assessmentTypes.includes(t) && VISIBLE_ADD_TYPES.includes(t),
  );

  const projectName = (project && project.name) || 'Project';

  return (
    <>
      {isLoading ? (
        <Layout
          hasNavigation
          render={
            <LoaderWrapper>
              <Loader />
            </LoaderWrapper>
          }
        />
      ) : (
        <Layout
          linkElement={Link}
          hasNavigation
          render={
            <div style={{ padding: `0 ${Spacing.l}`, maxWidth: 600 }}>
              <BackLink onClick={() => history.push('/projects')}>
                &larr; Back to Projects
              </BackLink>

              <Text size="xl" bold lineHeight={1.1}>
                {projectName}
              </Text>

              <Section>
                <SectionTitle size="s" bold>
                  Assessments
                </SectionTitle>

                {assessmentTypes.length === 0 && (
                  <Text size="s" style={{ opacity: 0.6, marginBottom: Spacing.m }}>
                    No assessments yet. Add one below to get started.
                  </Text>
                )}

                {assessmentTypes.map(type => {
                  const config = TYPES_CONFIG[type];
                  if (!config) return null;
                  return (
                    <AssessmentCard key={type}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <TypeIndicator color={config.color} />
                        <TypeLabel>{config.label}</TypeLabel>
                      </div>
                      <OpenButton color={config.color} onClick={() => handleOpen(type)}>
                        Open &rarr;
                      </OpenButton>
                    </AssessmentCard>
                  );
                })}
              </Section>

              {availableTypes.length > 0 && (
                <Section>
                  <SectionTitle size="s" bold>
                    Add Assessment
                  </SectionTitle>
                  <div>
                    {availableTypes.map(type => {
                      const config = TYPES_CONFIG[type];
                      return (
                        <AddButton
                          key={type}
                          color={config.color}
                          onClick={() => handleAddType(type)}>
                          {config.addLabel}
                        </AddButton>
                      );
                    })}
                  </div>
                </Section>
              )}
            </div>
          }
        />
      )}
    </>
  );
};

export default ProjectHub;
